import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Stack,
  Alert
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

const UserDefaultsForm = ({ defaults, onSave }) => {
  const [formData, setFormData] = useState({
    default_role: 'user',
    auto_approve: false,
    require_email_verification: true,
    ...defaults
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFormData(prev => ({ ...prev, ...defaults }));
  }, [defaults]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Stack spacing={3}>
        <FormControl fullWidth>
          <InputLabel>Default Role for New Users</InputLabel>
          <Select
            value={formData.default_role}
            label="Default Role for New Users"
            onChange={(e) => handleChange('default_role', e.target.value)}
          >
            <MenuItem value="user">User (Standard access)</MenuItem>
            <MenuItem value="admin">Admin (Can manage competitions)</MenuItem>
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Role assigned to users who sign up without an invite
          </Typography>
        </FormControl>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={formData.auto_approve}
                onChange={(e) => handleChange('auto_approve', e.target.checked)}
              />
            }
            label="Auto-approve new users"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
            If enabled, new users can immediately access the system. If disabled, an admin must approve them first.
          </Typography>
          {formData.auto_approve && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Auto-approve is enabled. Anyone who signs up will have immediate access.
            </Alert>
          )}
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={formData.require_email_verification}
                onChange={(e) => handleChange('require_email_verification', e.target.checked)}
              />
            }
            label="Require email verification"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
            Users must verify their email address before accessing the system
          </Typography>
          {!formData.require_email_verification && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Email verification is disabled. Users can sign in without verifying their email.
            </Alert>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default UserDefaultsForm;
